import { SvgIcon } from '@progress/kendo-react-common'
import { MouseEventHandler, useEffect, useRef, useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import useMeasure from 'react-use-measure'
import { HoverIcon } from '../../GlobalComponents/_icons/HoverIcon'

import { PACustomerContactViewRow } from '../../features/customer/customerSlice'
import { EditPencil } from '../../GlobalComponents/_icons/Icons'
import YellowTriangleWarning from '../../GlobalComponents/_icons/YellowTriangleWarning.svg'
import { InputLoader } from '../../GlobalComponents/Inputs/Inputs'
import { TextOverflow } from '../../GlobalComponents/_misc/TextOverflow'
import styles from './CustomerAccountCard.module.scss'

interface CustomerAccountCardProps {
    customer: PACustomerContactViewRow
    loadingInfo: Partial<PACustomerContactViewRow>
    openIdentifierUpdate(): void
    openCustomerTypeModal(): void
    openUniqueCustomerNameModal(): void
    onSelect(): MouseEventHandler<HTMLDivElement> | undefined
}

// TODO correct Identifier?
function CustomerAccountCard(props: CustomerAccountCardProps) {
    const {
        onSelect,
        openIdentifierUpdate,
        openCustomerTypeModal,
        openUniqueCustomerNameModal,
        customer,
        loadingInfo,
    } = props

    let toProperCase = (s: string | undefined) => {
        return s?.replace(
            /\w\S*/g,
            (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
        )
    }

    return (
        <div className={styles.cardContainer} data-testid='customerAccountCardContainer'>
            <div
                className={styles.selectButton}
                onClick={onSelect}
                data-testid='selectCustomerButton'
            >
                Select Customer
            </div>
            <div style={{ marginTop: 10 }}>
                <div className={styles.customerLine}>
                    {customer.CustomerName || customer.BillToName}
                    <span
                        className={styles.sectionAction}
                        onClick={openUniqueCustomerNameModal}
                        style={{ paddingLeft: 5 }}
                        data-testid='customerAccountCardCustomerNameUpdate'
                    >
                        {customer.CustomerName ? (
                            <SvgIcon icon={EditPencil} height={15} />
                        ) : (
                            <img
                                src={YellowTriangleWarning}
                                alt='Warning'
                                height={18}
                                style={{ position: 'absolute' }}
                            />
                        )}
                    </span>
                </div>
                <div className={styles.customerLine}>Account: {customer.AccountCode.trim()}</div>
                <div
                    className={styles.customerStatusRow}
                    data-testid='customerAccountCardStatusRow'
                >
                    <div className={styles.customerStatus}>
                        {loadingInfo.ReasonName ? (
                            <span style={{ color: 'var(--fadedText)' }}>
                                {/* {`${loadingInfo.ReasonName}${
                                    loadingInfo.CustomerReasonQualifier &&
                                    `-${customer.CustomerReasonQualifier}`
                                }`} */}
                                <InputLoader
                                    style={{
                                        maxHeight: 15,
                                        marginLeft: 10,
                                        width: 15,
                                        display: 'inline-flex',
                                    }}
                                />
                            </span>
                        ) : (
                            <div style={{ display: 'flex' }}>
                                <div>Identifier:&nbsp;</div>
                                <TextOverflow
                                    text={`${customer.ReasonName}${
                                        customer.CustomerReasonQualifier &&
                                        `-${customer.CustomerReasonQualifier}`
                                    }`}
                                    font={'14px roboto'}
                                    maxWidth={200}
                                    bubbleMinWidth={150}
                                />
                                {customer.ReasonName === 'Uncertain' && (
                                    <HoverIcon
                                        renderIcon={() => (
                                            <span className={styles.uncertainIcon}></span>
                                        )}
                                    >
                                        {(closeBubble: () => void) => (
                                            <div className={styles.identifierBubble}>
                                                Account Identifier has not been defined. Please
                                                <span
                                                    className={styles.sectionAction}
                                                    onClick={(e: any) => {
                                                        e.stopPropagation()
                                                        closeBubble()
                                                        openIdentifierUpdate()
                                                    }}
                                                >
                                                    {' '}
                                                    update
                                                </span>
                                                .
                                            </div>
                                        )}
                                    </HoverIcon>
                                )}
                            </div>
                        )}

                        {/* <div className={styles.hoveringDiv}> Test </div> */}
                    </div>
                    <div
                        className={styles.sectionAction}
                        onClick={openIdentifierUpdate}
                        style={{ paddingLeft: 5 }}
                        data-testid='customerAccountCardUpdate'
                    >
                        <SvgIcon
                            data-testid='customerAccountCardEditPencil'
                            icon={EditPencil}
                            height={15}
                        />
                    </div>
                </div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.billToSection}>
                <div className={styles.billToRow}>Bill to:</div>
                <div className={styles.customerLine}>
                    {customer.BillToName} ({customer.AccountCode.trim()})
                </div>
                <div>{customer.BillToAddress1}</div>
                <div>{customer.BillToAddress2}</div>
                <div>
                    {customer.BillToCity}
                    {customer.BillToCity ? ',' : ''} {customer.BillToState} {customer.BillToZipcode}
                </div>
                <div>{customer.BillToCountryName}</div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.shipToSection}>
                <div className={styles.shipToRow}>
                    <div>Ship to:</div>
                </div>
                <div className={styles.customerLine}>{customer.ShipToName}</div>
                <div>{customer.ShipToAddress1}</div>
                <div>{customer.ShipToAddress2}</div>
                <div>
                    {customer.ShipToCity}
                    {customer.ShipToCity ? ',' : ''} {customer.ShipToState} {customer.ShipToZipcode}
                </div>
                <div>{customer.ShipToCountryName}</div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.metaSection}>
                <div
                    style={{
                        fontWeight: 400,
                        // maxWidth: 290,
                    }}
                >
                    <div style={{ display: 'flex' }}>
                        Customer Type:&nbsp;
                        <TextOverflow
                            text={customer.CustomerTypeName || ''}
                            font={'14px roboto'}
                            maxWidth={200}
                            bubbleMinWidth={150}
                        />
                        <div
                            className={styles.sectionAction}
                            onClick={openCustomerTypeModal}
                            style={{ paddingLeft: 5 }}
                            data-testid='customerAccountCardCustomerTypeUpdate'
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </div>
                    </div>
                </div>
                <div data-testid='lastTransaction'>
                    Last Transaction:{' '}
                    {customer.LastTransaction
                        ? new Date(customer.LastTransaction).toLocaleDateString() === '1/1/1900'
                            ? 'None'
                            : new Date(customer.LastTransaction).toLocaleDateString()
                        : ''}
                </div>
                <div>Terms: {customer.Terms}</div>
                <div>
                    Credit Limit:{' '}
                    {(customer.CreditLimit || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    })}
                </div>
                {customer.IntimateIAMUsername && (
                    <div style={{ fontWeight: 400, marginTop: 20 }}>
                        <div>Account questions?</div>
                        <div>
                            Chat with:{' '}
                            <a href={`im:${customer.IntimateIAMUsername}@awc-inc.com`}>
                                {toProperCase(customer.IntimateIAMNickname)}{' '}
                                {toProperCase(customer.IntimateIAMLastName)}
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CustomerAccountCard
